/**
 * Branch-aware upgrade-path resolution. A unit upgrades through its shared base
 * `tiers`; after the last base tier it picks ONE `branch`, whose tiers continue
 * the chain. Everything downstream (stats, blockCount, perks, cost, max tier)
 * is computed over the *effective* tier chain for the chosen path, so the rest
 * of the sim treats a branched unit exactly like a linear one.
 *
 * Engine-agnostic (no Phaser) so it stays headlessly testable.
 */
import type { Unit as UnitDef, UnitTier } from '@/data/schema/unit';

/** The full ordered tier chain for a unit on a given branch (base ++ branch). */
export function effectiveTiers(def: UnitDef, branchId: string | null): UnitTier[] {
  if (!branchId) return def.tiers;
  const branch = def.branches.find((b) => b.id === branchId);
  return branch ? [...def.tiers, ...branch.tiers] : def.tiers;
}

/** Highest tier index reachable on the current path. */
export function maxTierFor(def: UnitDef, branchId: string | null): number {
  return effectiveTiers(def, branchId).length - 1;
}

/** True if the next upgrade from `tier` is the one-time branch choice. */
export function isBranchPoint(
  def: UnitDef,
  tier: number,
  branchId: string | null,
): boolean {
  return !branchId && def.branches.length > 0 && tier === def.tiers.length - 1;
}

/**
 * The level shown to the player. The basic form counts 1..N over the base
 * tiers; once the unit branches into its advanced form, the level RESTARTS at 1
 * and counts 1..M over the branch tiers (so "Footmen Lv4 → Shieldwall Lv1").
 */
export function displayLevel(def: UnitDef, tier: number, branchId: string | null): number {
  return branchId ? tier - def.tiers.length + 1 : tier + 1;
}

/** The tier definition active at `tier` on the chosen path (or undefined if past max). */
export function tierDefAt(
  def: UnitDef,
  tier: number,
  branchId: string | null,
): UnitTier | undefined {
  return effectiveTiers(def, branchId)[tier];
}
